import { differenceInDays, formatISO, parseISO } from "date-fns"
import slugify from "slugify"

import { COUNTRIES_MAPPER, STATES_MAPPER } from "configs/country-state-names"
import { BookingInitialState } from "stores/booking/types"
import { SearchInitialState } from "stores/search/types"
import { getRefTaToken } from "utils/local-storage"

import { capitalize } from "./misc"

export const capitalizeFirstLetter = (string: string) =>
  string.charAt(0).toUpperCase() + string.slice(1)

export const customSlugify = (word: string, replacement: string = "") => {
  if (!word || typeof word !== "string") {
    return "" // Return an empty string if the input is not a valid string
  }
  return slugify(
    word
      // eslint-disable-next-line regexp/no-obscure-range
      ?.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\d ]/g, replacement)
      ?.trim()
      ?.replace(/ /g, "-")
      ?.replace(/-+/g, "-"),
    {
      lower: false,
    },
  )
}

export const slugifyForLocationName = (
  word: string,
  replacement: string = "-",
) => {
  return (
    word
      // removed below logic to remove . as it was not matching with backend polygon name (eg. St. Augustine, FL, USA)
      // ?.replace(/\./g, "") // to remove this . from name "D. H. Hill Jr. Library" https://tasks.softyoi.com/projects/softyoi/boards/62/details/3555/overview
      // eslint-disable-next-line regexp/no-obscure-range
      ?.normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      ?.trim()
      ?.replace(/ /g, replacement)
  )
}

export const normalizeText = (text: string) => {
  return text?.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
}

export const formatDateYYYYMMDD = (date: string) => {
  try {
    return formatISO(parseISO(date), {
      representation: "date",
    })
  } catch (e) {
    return ""
  }
}

export function buildDetailPageUrl({
  startDate,
  endDate,
  adultCount,
  childrenCount,
  petNumber,
  petAllowed,
  propertyId,
  name,
  propertyName,
  eventAllowed,
  smokeAllowed,
  childAllowed,
  FeaturesAndAmenities,
  sortBy,
  minBedRoom,
  minBed,
  minBathRoom,
  minPrice,
  maxPrice,
  toggleOn,
  isGoogle,
  isDirect,
  whimReferral,
  fromMultiUnit,
  cancellable,
  searchPrice,
  savedPrice,
  strikePrice,
  source,
  page,
  quoteId,
  allowSameDay,
}: {
  adultCount?: number
  childrenCount?: number
  petNumber?: number
  endDate?: string | null // "1983-12-31"
  name?: string
  petAllowed?: boolean
  propertyId: string
  propertyName: string
  startDate?: string | null // "1983-12-31"
  eventAllowed?: boolean
  smokeAllowed?: boolean
  childAllowed?: boolean
  FeaturesAndAmenities?: string | null
  sortBy?: string
  minBedRoom?: string | null
  minBed?: string | null
  minBathRoom?: string | null
  minPrice?: string | null
  maxPrice?: string | null
  toggleOn?: boolean | null
  isGoogle?: string | null
  isDirect?: string | null
  whimReferral?: string | null
  fromMultiUnit?: string // check if the url is for multi unit property or not
  cancellable?: boolean
  searchPrice?: string | null
  savedPrice?: string | null
  strikePrice?: string | null
  source?: string | null // e.g. "homepage", "search", etc.
  page?: string | null
  quoteId?: string | null
  allowSameDay?: boolean
}) {
  const query = new URLSearchParams()

  if (name) query.set("name", name)

  if (startDate && endDate) {
    const startDateFormatted = formatDateYYYYMMDD(startDate)
    const endDateFormatted = formatDateYYYYMMDD(endDate)
    if (startDateFormatted && endDateFormatted) {
      query.set("check_in", startDateFormatted)
      query.set("check_out", endDateFormatted)
    }
  }
  query.set("adultsCount", `${adultCount || 1}`)
  query.set("childrenCount", `${childrenCount || 0}`)
  query.set("petNumber", `${petNumber || 0}`)
  query.set("suitablePet", petAllowed ? "true" : "false")
  const timestamp = new Date().getTime()
  query.set("timestamp", timestamp.toString())
  if (FeaturesAndAmenities) {
    query.set("FeaturesAndAmenities", FeaturesAndAmenities)
  }
  query.set("eventAllowed", eventAllowed ? "true" : "false")
  query.set("smokeAllowed", smokeAllowed ? "true" : "false")
  query.set("childAllowed", childAllowed ? "true" : "false")
  query.set("sortBy", sortBy || "")
  query.set("minBathRoom", minBathRoom || "")
  query.set("minBedRoom", minBedRoom || "")
  query.set("minBed", minBed || "")
  query.set("minPrice", minPrice || "")
  query.set("maxPrice", maxPrice || "")
  query.set("cancellable", cancellable ? "true" : "false")
  query.set("searchPrice", searchPrice || "")
  query.set("strikePrice", strikePrice || "")
  query.set("toggleOn", toggleOn ? "true" : "false")
  query.set("page", page || "")
  if (isGoogle) query.set("isgoogle", isGoogle ? "true" : "false")
  if (isDirect) query.set("isdirect", isDirect ? "true" : "false")
  if (whimReferral) query.set("whimreferral", whimReferral || "")
  if (source) query.set("source", source || "")
  if (savedPrice) query.set("savedPrice", savedPrice || "")
  if (quoteId) query.set("quoteId", quoteId || "")
  if (allowSameDay) query.set("allowSameDay", "true")

  // check if the url is for multi unit property or not
  if (fromMultiUnit) query.set("fromMultiUnit", fromMultiUnit)

  const refTaToken = getRefTaToken()
  if (refTaToken) query.set("ref_ta", refTaToken)

  query.set("allInPriceToggle", "true")

  const url = `/detail/${
    customSlugify(propertyName) || "Whimstay"
  }/${propertyId}?${query.toString()}`
  return url
}

export function buildDetailPageUrlWithoutParams({
  propertyId,
  propertyName,
}: {
  propertyId: string
  propertyName: string
}) {
  const url = `/detail/${
    customSlugify(propertyName) || "Whimstay"
  }/${propertyId}`
  return url
}

export function buildMultiUnitDetailPageUrl({
  name,
  startDate,
  endDate,
  adultCount,
  childrenCount,
  petAllowed,
  propertyId,
  propertyName,
  multiUnitGroupId,
  eventAllowed,
  smokeAllowed,
  childAllowed,
  FeaturesAndAmenities,
  sortBy,
  minBedRoom,
  minBed,
  minBathRoom,
  minPrice,
  maxPrice,
  isGoogle,
  isDirect,
  whimReferral,
  cancellable,
  page,
  allInPriceToggle,
  searchPrice,
  savedPrice,
  strikePrice,
  allowSameDay,
}: {
  name?: string
  adultCount?: number | string
  childrenCount?: number | string
  endDate?: string | null // "1983-12-31"
  petAllowed?: boolean
  propertyId: string
  propertyName: string
  multiUnitGroupId: string
  startDate?: string | null // "1983-12-31"
  eventAllowed?: boolean
  smokeAllowed?: boolean
  childAllowed?: boolean
  FeaturesAndAmenities?: string | null
  sortBy?: string
  minBedRoom?: string | null
  minBed?: string | null
  minBathRoom?: string | null
  minPrice?: string | null
  maxPrice?: string | null
  isGoogle?: string | null
  isDirect?: string | null
  whimReferral?: string | null
  cancellable?: boolean
  page?: string | null
  allInPriceToggle?: boolean
  searchPrice?: string | null
  savedPrice?: string | null
  strikePrice?: string | null
  allowSameDay?: boolean
}) {
  const query = new URLSearchParams()
  if (name) query.set("name", name)
  if (startDate && endDate) {
    const startDateFormatted = formatDateYYYYMMDD(startDate)
    const endDateFormatted = formatDateYYYYMMDD(endDate)
    if (startDateFormatted && endDateFormatted) {
      query.set("check_in", startDateFormatted)
      query.set("check_out", endDateFormatted)
    }
  }
  query.set("adultsCount", `${adultCount || 1}`)
  query.set("childrenCount", `${childrenCount || 0}`)
  query.set("suitablePet", petAllowed ? "true" : "false")
  const timestamp = new Date().getTime()
  query.set("timestamp", timestamp.toString())
  if (FeaturesAndAmenities) {
    query.set("FeaturesAndAmenities", FeaturesAndAmenities)
  }
  query.set("eventAllowed", eventAllowed ? "true" : "false")
  query.set("smokeAllowed", smokeAllowed ? "true" : "false")
  query.set("childAllowed", childAllowed ? "true" : "false")
  query.set("sortBy", sortBy || "")
  query.set("minBathRoom", minBathRoom || "")
  query.set("minBedRoom", minBedRoom || "")
  query.set("minBed", minBed || "")
  query.set("minPrice", minPrice || "")
  query.set("maxPrice", maxPrice || "")
  query.set("cancellable", cancellable ? "true" : "false")
  query.set("page", page || "")
  if (isGoogle) query.set("isgoogle", isGoogle ? "true" : "false")
  if (isDirect) query.set("isdirect", isDirect ? "true" : "false")
  if (whimReferral) query.set("whimreferral", whimReferral || "")

  query.set("allInPriceToggle", allInPriceToggle ? "true" : "false")
  if (searchPrice) query.set("searchPrice", searchPrice || "")
  if (strikePrice) query.set("strikePrice", strikePrice || "")
  if (savedPrice) query.set("savedPrice", savedPrice || "")
  if (allowSameDay) query.set("allowSameDay", "true")

  const url = `/multi-unit-detail/${
    customSlugify(propertyName) || "Whimstay"
  }/${propertyId}/${multiUnitGroupId}?${query.toString()}`
  return url
}

export const dashedToCommaSeparatedName = (name: string) => {
  return name?.replace(/--/g, ", ")?.replace(/-/g, " ")
}

export const ensembleCityStateCountry = ({
  city,
  country,
  state,
}: {
  city?: string
  state?: string
  country?: string
}) => {
  const result: string[] = []
  if (city) result.push(city)
  if (state) result.push(state)
  if (country) result.push(country)
  return result.join(", ") || ""
}

function isState(value: string): boolean {
  return !!STATES_MAPPER[value.toUpperCase() as keyof typeof STATES_MAPPER]
}

function isCountry(value: string): boolean {
  const normalized = value.toLowerCase()
  return (
    normalized === "us" ||
    normalized === "usa" ||
    normalized === "united states" ||
    normalized === "canada" ||
    normalized === "mexico"
  )
}

export function createSearchURL(location: string): string {
  const parts = location
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean)

  if (!parts.length) return ""

  const cityify = (v: string) => capitalize(customSlugify(v))
  const resolveState = (v: string) =>
    capitalize(
      customSlugify(STATES_MAPPER[v as keyof typeof STATES_MAPPER] || v),
    )

  const resolveCountry = (v: string) =>
    capitalize(
      customSlugify(COUNTRIES_MAPPER[v as keyof typeof COUNTRIES_MAPPER] || v),
    )

  /** ---------- 3 PARTS ---------- */
  if (parts.length >= 3) {
    return [
      cityify(parts[0]),
      resolveState(parts[1]),
      resolveCountry(parts[2]),
    ].join("--")
  }

  /** ---------- 2 PARTS ---------- */
  if (parts.length === 2) {
    const city = parts[0].trim()
    const second = parts[1].trim()

    const stateKey = second.toUpperCase()

    // city, state
    if (STATES_MAPPER[stateKey as keyof typeof STATES_MAPPER]) {
      return [
        customSlugify(city),
        customSlugify(STATES_MAPPER[stateKey as keyof typeof STATES_MAPPER]),
      ].join("--")
    }

    // city, country (explicit only)
    if (isCountry(second)) {
      return [
        customSlugify(city),
        customSlugify(
          COUNTRIES_MAPPER[second as keyof typeof COUNTRIES_MAPPER] || second,
        ),
      ].join("--")
    }

    // fallback
    return customSlugify(city)
  }

  /** ---------- 1 PART ---------- */
  const single = parts[0]

  if (isCountry(single)) {
    return resolveCountry(single)
  }

  if (isState(single)) {
    return resolveState(single)
  }

  // fallback city
  return cityify(single)
}

export const updateQueryStringParameter = (
  uri: string,
  key: string,
  value: string | number,
) => {
  const re = new RegExp(`([?&])${key}=.*?(&|$)`, "i")
  const separator = uri.indexOf("?") !== -1 ? "&" : "?"
  if (uri.match(re)) {
    return uri.replace(re, `$1${key}=${value}$2`)
  }
  return `${uri + separator + key}=${value}`
}

export const clearQueryStringParameter = (uri: string) => {
  return uri.replace(/#.*$/, "").replace(/\?.*$/, "")
}

export const validEmailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-z\-\d]+\.)+[a-z]{2,}))$/i

export function removeQueryParameters(url: string, paramsToRemove: string[]) {
  const urlObject = new URL(url)
  paramsToRemove.forEach((param) => urlObject.searchParams.delete(param))
  return urlObject.href
}

export const getQueryParams = (
  bookingStoreParams: BookingInitialState,
  searchParams: URLSearchParams | null,
  // property: PropertyTypeV2,
) => {
  const queryParams: string[] = []
  const adultsCount = searchParams?.get("adultsCount") || 2
  const childrenCount = searchParams?.get("childrenCount") || 0
  const startDate =
    searchParams?.get("check_in") || bookingStoreParams.startDate || ""
  const endDate =
    searchParams?.get("check_out") || bookingStoreParams.endDate || ""

  if (startDate && endDate) {
    const startDateFormatted = formatDateYYYYMMDD(startDate)
    const endDateFormatted = formatDateYYYYMMDD(endDate)

    if (startDateFormatted && endDateFormatted) {
      queryParams.push(`?startDate=${startDateFormatted}`)
      queryParams.push(`endDate=${endDateFormatted}`)
      const noOfDays = differenceInDays(
        parseISO(endDateFormatted),
        parseISO(startDateFormatted),
      )
      queryParams.push(`noOfDays=${noOfDays || ""}`)
    }
  }

  queryParams.push(`adultCount=${adultsCount || ""}`)
  queryParams.push(`childrenCount=${childrenCount || ""}`)
  queryParams.push(`petAllowed=${searchParams?.get("suitablePet") || ""}`)
  queryParams.push(
    `numberOfGuest=${
      Number(adultsCount) + Number(childrenCount) ||
      searchParams?.get("numberOfGuest") ||
      ""
    }`,
  )
  queryParams.push(`eventAllowed=${searchParams?.get("eventAllowed") ?? ""}`)
  queryParams.push(`smokeAllowed=${searchParams?.get("smokeAllowed") ?? ""}`)
  queryParams.push(`childAllowed=${searchParams?.get("childAllowed") ?? ""}`)
  queryParams.push(
    `FeaturesAndAmenities=${decodeURIComponent(
      searchParams?.get("FeaturesAndAmenities") ?? "",
    )}`,
  )
  queryParams.push(`sortBy=${searchParams?.get("sortBy") ?? ""}`)
  queryParams.push(`minBedRoom=${searchParams?.get("minBedRoom") ?? ""}`)
  queryParams.push(`minBed=${searchParams?.get("minBed") ?? ""}`)
  queryParams.push(`minBathRoom=${searchParams?.get("minBathRoom") ?? ""}`)
  queryParams.push(`minPrice=${searchParams?.get("minPrice") ?? ""}`)
  queryParams.push(`maxprice=${searchParams?.get("maxPrice") ?? ""}`)
  queryParams.push(`cancellable=${searchParams?.get("cancellable") ?? ""}`)
  queryParams.push(`page=${searchParams?.get("page") ?? ""}`)
  if (searchParams?.get("allowSameDay") === "true") {
    queryParams.push("allowSameDay=true")
  }

  return queryParams.join("&")
}

export const getRecentSearchParams = (item: {
  name: string
  checkInDate: string | null
  checkOutDate: string | null
  adultCount: number
  petAllowed: boolean
  childCount: number
}) => {
  const queryParams: string[] = []

  queryParams.push(`?name=${item.name}`)

  if (item.checkInDate && new Date(item.checkInDate) >= new Date()) {
    if (item.checkInDate) queryParams.push(`startDate=${item.checkInDate}`)
    if (item.checkOutDate) queryParams.push(`endDate=${item.checkOutDate}`)
    queryParams.push(
      `noOfDays=${differenceInDays(
        new Date(item?.checkOutDate || ""),
        new Date(item?.checkInDate || ""),
      )}`,
    )
  }

  queryParams.push(`adultCount=${item.adultCount}`)
  queryParams.push(`childrenCount=${item.childCount}`)
  queryParams.push(`numberOfGuest=${item.adultCount + item.childCount}`)
  queryParams.push(`petAllowed=${item.petAllowed}`)

  return queryParams.join("&")
}

export const makeCDNUrl = (url: string) => {
  return `${
    process.env.NEXT_PUBLIC_BUCKET_HOST ||
    process.env.NEXT_PUBLIC_ORIGIN_URL ||
    ""
  }${url}`
}

export const makeBaseUrl = (url: string) => {
  return `${
    process.env.NEXT_PUBLIC_ORIGIN_URL ||
    "https://www.signaturestaycollection.com"
  }${url}`
}

// Generates a vacation rental search URL based on the provided search parameters.
export const generateVacationRentalUrl = (
  locationName: string,
  search: SearchInitialState["state"],
): string => {
  const formattedName = dashedToCommaSeparatedName(locationName || "")
    .split(" ")
    .map((word) => capitalizeFirstLetter(word))
    .join(" ")

  // Creates URL search parameters from the search object.
  const urlParams = new URLSearchParams({
    startDate: search?.startDate || "",
    endDate: search?.endDate || "",
    name: formattedName || "",
    adultCount: (search?.adultCount || 0).toString(),
    childAllowed: (search?.childAllowed || false).toString(),
    childrenCount: (search?.childrenCount || 0).toString(),
    petAllowed: (search?.petAllowed || false).toString(),
    noOfDays: search?.noOfDays?.toString() || "",
  })

  // Retrieves the location information based on the city.
  const location = ensembleCityStateCountry({
    city: formattedName || "",
  })

  // Returns the complete URL with the base, location, and query parameters.
  return `/vacation-rentals/${createSearchURL(
    location,
  )}?${urlParams.toString()}`
}
